/**
 * Memoket Theme - Main JavaScript
 * General UI behaviors and utilities
 *
 * @version 1.0.0
 */

(function() {
  'use strict';

  var MemoketTheme = {
    /**
     * Initialize theme
     */
    init: function() {
      this.initHeader();
      this.initMobileMenu();
      this.initSmoothScroll();
      this.initAnimations();
      this.initLazyLoading();
      this.initFAQ();
      this.initTabs();
      this.initSwipers();
      this.initQuickBuy();
      this.initCartPermalinkTracking();
    },

    /**
     * Header scroll behavior
     */
    initHeader: function() {
      var header = document.querySelector('[data-header]');
      if (!header) return;

      var lastScroll = 0;
      var scrollThreshold = 100;

      var handleScroll = function() {
        var currentScroll = window.pageYOffset || document.documentElement.scrollTop;

        // Add shadow on scroll
        if (currentScroll > scrollThreshold) {
          header.classList.add('shadow-md', 'bg-white/95', 'backdrop-blur-sm');
        } else {
          header.classList.remove('shadow-md', 'bg-white/95', 'backdrop-blur-sm');
        }

        lastScroll = currentScroll;
      };

      // Throttle scroll handler
      var ticking = false;
      window.addEventListener('scroll', function() {
        if (!ticking) {
          window.requestAnimationFrame(function() {
            handleScroll();
            ticking = false;
          });
          ticking = true;
        }
      }, { passive: true });
    },

    /**
     * Mobile menu toggle
     */
    initMobileMenu: function() {
      var menuToggle = document.querySelector('[data-mobile-menu-toggle]');
      var menu = document.querySelector('[data-mobile-menu]');
      var menuClose = document.querySelector('[data-mobile-menu-close]');

      if (!menuToggle || !menu) return;

      var openMenu = function() {
        menu.classList.remove('hidden', 'opacity-0');
        menu.classList.add('opacity-100');
        document.body.style.overflow = 'hidden';
        menuToggle.setAttribute('aria-expanded', 'true');
      };

      var closeMenu = function() {
        menu.classList.add('opacity-0');
        menu.classList.remove('opacity-100');
        setTimeout(function() {
          menu.classList.add('hidden');
        }, 200);
        document.body.style.overflow = '';
        menuToggle.setAttribute('aria-expanded', 'false');
      };

      menuToggle.addEventListener('click', function(e) {
        e.preventDefault();
        if (menu.classList.contains('hidden')) {
          openMenu();
        } else {
          closeMenu();
        }
      });

      if (menuClose) {
        menuClose.addEventListener('click', closeMenu);
      }

      // Close on ESC key
      document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && !menu.classList.contains('hidden')) {
          closeMenu();
        }
      });

      // Close on outside click
      menu.addEventListener('click', function(e) {
        if (e.target === menu) {
          closeMenu();
        }
      });
    },

    /**
     * Smooth scroll for anchor links
     */
    initSmoothScroll: function() {
      var anchors = document.querySelectorAll('a[href^="#"]');
      var header = document.querySelector('[data-header]');

      anchors.forEach(function(anchor) {
        anchor.addEventListener('click', function(e) {
          var targetId = anchor.getAttribute('href');
          if (targetId === '#' || targetId === '#top') {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
          }

          var target = document.querySelector(targetId);
          if (target) {
            e.preventDefault();

            var headerHeight = header ? header.offsetHeight : 0;
            var targetPosition = target.getBoundingClientRect().top + window.pageYOffset - headerHeight - 20;

            window.scrollTo({
              top: targetPosition,
              behavior: 'smooth'
            });

            // Update URL without jumping
            if (history.pushState) {
              history.pushState(null, null, targetId);
            }
          }
        });
      });
    },

    /**
     * Initialize scroll-triggered animations
     */
    initAnimations: function() {
      if (!('IntersectionObserver' in window)) return;

      var animatedElements = document.querySelectorAll('[data-animate]');
      if (animatedElements.length === 0) return;

      var observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
            observer.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      });

      animatedElements.forEach(function(el) {
        observer.observe(el);
      });
    },

    /**
     * Initialize native lazy loading fallback
     */
    initLazyLoading: function() {
      // Native lazy loading is supported in modern browsers
      // This provides a fallback for older browsers
      if ('loading' in HTMLImageElement.prototype) {
        return; // Native lazy loading supported
      }

      // Polyfill for older browsers
      if ('IntersectionObserver' in window) {
        var lazyImages = document.querySelectorAll('img[loading="lazy"]');

        var imageObserver = new IntersectionObserver(function(entries) {
          entries.forEach(function(entry) {
            if (entry.isIntersecting) {
              var img = entry.target;
              if (img.dataset.src) {
                img.src = img.dataset.src;
              }
              if (img.dataset.srcset) {
                img.srcset = img.dataset.srcset;
              }
              img.classList.add('loaded');
              imageObserver.unobserve(img);
            }
          });
        });

        lazyImages.forEach(function(img) {
          imageObserver.observe(img);
        });
      }
    },

    /**
     * Utility: Debounce function
     */
    debounce: function(func, wait, immediate) {
      var timeout;
      return function() {
        var context = this;
        var args = arguments;
        var later = function() {
          timeout = null;
          if (!immediate) func.apply(context, args);
        };
        var callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) func.apply(context, args);
      };
    },

    /**
     * Initialize FAQ accordion (v3.0 - Independent toggle, no auto-close)
     * Icon: right (0deg) = closed, down (90deg) = open
     */
    initFAQ: function() {
      var faqItems = document.querySelectorAll('[data-faq-item]');
      if (faqItems.length === 0) return;

      faqItems.forEach(function(item) {
        var trigger = item.querySelector('[data-faq-trigger]');
        var content = item.querySelector('[data-faq-content]');
        var icon = item.querySelector('.faq-icon');

        if (!trigger || !content) return;

        // Skip if already initialized (by inline script)
        if (trigger.dataset.initialized) return;
        trigger.dataset.initialized = 'true';

        trigger.addEventListener('click', function() {
          var isOpen = trigger.getAttribute('aria-expanded') === 'true';

          // Toggle current item only (independent toggle, no auto-close)
          if (isOpen) {
            trigger.setAttribute('aria-expanded', 'false');
            content.style.maxHeight = '0';
            content.setAttribute('aria-hidden', 'true');
            if (icon) icon.style.transform = 'rotate(0deg)';
          } else {
            trigger.setAttribute('aria-expanded', 'true');
            content.style.maxHeight = content.scrollHeight + 'px';
            content.setAttribute('aria-hidden', 'false');
            if (icon) icon.style.transform = 'rotate(90deg)';
          }
        });

        // Keyboard support
        trigger.addEventListener('keydown', function(e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            trigger.click();
          }
        });
      });
    },

    /**
     * Initialize tab switching
     */
    initTabs: function() {
      var triggers = document.querySelectorAll('[data-tab-trigger]');
      var contents = document.querySelectorAll('[data-tab-content]');

      if (triggers.length === 0 || contents.length === 0) return;

      triggers.forEach(function(trigger) {
        // Skip if already initialized
        if (trigger.dataset.initialized) return;
        trigger.dataset.initialized = 'true';

        trigger.addEventListener('click', function() {
          var targetId = trigger.getAttribute('data-tab-trigger');

          // Update triggers
          triggers.forEach(function(t) {
            var isActive = t.getAttribute('data-tab-trigger') === targetId;
            t.classList.toggle('active', isActive);
            t.classList.toggle('text-primary', isActive);
            t.classList.toggle('border-primary', isActive);
            t.setAttribute('aria-selected', isActive);
          });

          // Update contents
          contents.forEach(function(content) {
            var isActive = content.getAttribute('data-tab-content') === targetId;
            content.classList.toggle('hidden', !isActive);
          });
        });
      });
    },

    /**
     * Initialize Swiper carousels
     */
    initSwipers: function() {
      if (typeof Swiper === 'undefined') return;

      // Voice Capture Swiper
      var voiceCaptureSwiper = document.querySelector('[data-swiper="voice-capture"]');
      if (voiceCaptureSwiper && !voiceCaptureSwiper.swiper) {
        new Swiper(voiceCaptureSwiper, {
          slidesPerView: 1.15,
          spaceBetween: 16,
          centeredSlides: true,
          loop: false,
          pagination: {
            el: voiceCaptureSwiper.querySelector('.swiper-pagination'),
            clickable: true
          },
          breakpoints: {
            480: {
              slidesPerView: 1.3,
              spaceBetween: 20
            }
          }
        });
      }
    },

    cartRequest: function(url, options) {
      return fetch(url, options).then(function(response) {
        if (!response.ok) {
          return response.text().then(function(text) {
            var message = 'Cart request failed';

            try {
              var data = text ? JSON.parse(text) : {};
              message = data.description || data.message || data.error || message;
            } catch (error) {
              if (text) {
                message = text;
              }
            }

            throw new Error(message);
          });
        }

        return response.json();
      });
    },

    getCart: function() {
      return this.cartRequest('/cart.js', {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });
    },

    addCartItem: function(variantId, quantity) {
      return this.cartRequest('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: parseInt(variantId, 10),
            quantity: quantity
          }]
        })
      });
    },

    clearCart: function() {
      return this.cartRequest('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
    },

    changeCartLine: function(line, quantity) {
      return this.cartRequest('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          line: line,
          quantity: quantity
        })
      });
    },

    updateCartAttributes: function(attributes) {
      if (!attributes || Object.keys(attributes).length === 0) {
        return Promise.resolve(null);
      }

      return this.cartRequest('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          attributes: attributes
        })
      });
    },

    findCartLineByVariant: function(cart, variantId) {
      if (!cart || !cart.items || !cart.items.length) {
        return null;
      }

      var normalizedVariantId = parseInt(variantId, 10);

      for (var index = 0; index < cart.items.length; index++) {
        var item = cart.items[index];
        if (parseInt(item.id, 10) === normalizedVariantId || parseInt(item.variant_id, 10) === normalizedVariantId) {
          return {
            item: item,
            line: index + 1
          };
        }
      }

      return null;
    },

    buildDirectCheckoutAttributes: function() {
      var attributes = {};
      var ab = window.__MEMOKET_AB || {};
      // Prefer the active early-bird test; fall back to the legacy hero test if present.
      var testKey = ab.early_bird_test_v1 ? 'early_bird_test_v1'
                  : (ab.hero_test_v1 ? 'hero_test_v1' : null);
      if (testKey) {
        attributes.ab_test = testKey;
        attributes.ab_variant = ab[testKey];
      }

      // Device the buyer converted on (mobile vs desktop). note_attribute only — never a UTM.
      try {
        attributes.device = window.matchMedia('(max-width: 1023px)').matches ? 'mobile' : 'desktop';
      } catch (e) { /* matchMedia unavailable — omit device */ }

      // Meta EMQ — keep attaching fbp/fbc (do NOT regress).
      if (typeof window.getMetaPixelIdsForCAPI === 'function') {
        var metaIds = window.getMetaPixelIdsForCAPI();
        if (metaIds) {
          if (metaIds.fbp) attributes.fbp = metaIds.fbp;
          if (metaIds.fbc) attributes.fbc = metaIds.fbc;
        }
      }

      return attributes;
    },

    ensureDirectCheckoutCart: function(options) {
      var self = this;
      var variantId = parseInt(options && options.variantId, 10);
      var quantity = options && options.quantity ? parseInt(options.quantity, 10) : 1;
      var logPrefix = options && options.logPrefix ? options.logPrefix : '[Direct Checkout]';

      if (!variantId || Number.isNaN(variantId)) {
        return Promise.reject(new Error('Invalid variant ID'));
      }

      if (!quantity || Number.isNaN(quantity) || quantity < 1) {
        quantity = 1;
      }

      return this.getCart().then(function(cart) {
        var items = (cart && cart.items) || [];
        var existingLine = self.findCartLineByVariant(cart, variantId);
        var hasOnlyTargetVariant = items.length === 1 && existingLine && existingLine.item.quantity === quantity;

        if (hasOnlyTargetVariant) {
          console.log(logPrefix, 'Cart already contains only the target variant at qty', quantity, '- reusing');
          return cart;
        }

        console.log(logPrefix, 'Cart has', items.length, 'line(s); resetting to single target variant');
        return self.clearCart().then(function() {
          return self.addCartItem(variantId, quantity);
        }).then(function() {
          return self.getCart();
        });
      });
    },

    startDirectCheckout: function(options) {
      var self = this;
      var settings = options || {};
      var logPrefix = settings.logPrefix || '[Direct Checkout]';
      var attributesBuilder = settings.attributesBuilder || function() {
        return self.buildDirectCheckoutAttributes();
      };
      var onCartReady = settings.onCartReady;

      return this.ensureDirectCheckoutCart(settings)
        .then(function(cart) {
          if (typeof onCartReady === 'function') {
            onCartReady(cart);
          }

          var attributes = attributesBuilder(cart) || {};
          if (Object.keys(attributes).length === 0) {
            return cart;
          }

          console.log(logPrefix, 'Writing cart attributes:', attributes);
          return self.updateCartAttributes(attributes).then(function(updatedCart) {
            return updatedCart || cart;
          });
        })
        .then(function(cart) {
          console.log(logPrefix, 'Redirecting to checkout...');
          window.location.href = '/checkout';
          return cart;
        });
    },

    /**
     * Single-product checkout via cart permalink.
     *
     * Clears the cart, then navigates to /cart/{variantId}:1 — a Shopify cart
     * permalink that adds the variant and redirects straight to checkout.
     * Clearing first means the cart is effectively REPLACED, never
     * incremented, so the buyer always lands on checkout with exactly
     * quantity 1 of only this product — even after a back-button return.
     *
     * Cart attributes (AB test, Meta CAPI fbp/fbc) ride along on the
     * permalink as attributes[...] query params so they survive the replace.
     *
     * Returns a promise that rejects on an invalid variant or a failed
     * cart clear, so callers can surface an inline error instead of redirecting.
     */
    /**
     * Direct-checkout CTAs use a cart permalink, which bypasses the cart-add
     * API — the Meta channel app sees nothing on this funnel. Emit the Meta
     * events here, each as a browser + Lambda CAPI pair deduped by a shared
     * event_id:
     *   - EB_CTA_Click (custom): the primary ads-optimization event.
     *   - AddToCart (standard): zero-setup fallback optimization goal,
     *     1:1 with EB_CTA_Click on this funnel; keep-or-drop at D+7 review.
     * Gated on fbq presence so visitors without marketing consent send
     * neither side. Deliberately independent of meta_capi_enabled: that flag
     * also re-opens legacy analytics.js CAPI paths, which would double-count
     * ATC on product-form adds where the channel app sends its own pair.
     */
    trackDirectCheckoutEvents: function(settings) {
      if (typeof window.fbq !== 'function') return;

      var contentIds = settings.variantId ? [String(settings.variantId)] : [];
      var value = typeof settings.variantPrice === 'number' && settings.variantPrice > 0
        ? settings.variantPrice / 100
        : null;
      var currency = settings.currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      var capiUrl = window.MemoketConfig && window.MemoketConfig.metaCapiUrl;
      var metaIds = window.getMetaPixelIdsForCAPI ? window.getMetaPixelIdsForCAPI() : { fbp: '', fbc: '' };

      var baseParams = {
        content_type: 'product',
        content_ids: contentIds,
        num_items: 1
      };
      if (value !== null) {
        baseParams.value = value;
        baseParams.currency = currency;
      }

      function sendPair(eventName, isStandard) {
        var eventId = eventName.toLowerCase() + '_' + Date.now() + '_' + Math.random().toString(36).slice(2, 11);

        try {
          window.fbq(isStandard ? 'track' : 'trackCustom', eventName, baseParams, { eventID: eventId });
        } catch (e) {}

        if (!capiUrl) return;

        var payload = {
          event_name: eventName,
          event_id: eventId,
          event_time: Math.floor(Date.now() / 1000),
          url: window.location.href,
          user_agent: navigator.userAgent,
          fbp: metaIds.fbp,
          fbc: metaIds.fbc
        };
        Object.keys(baseParams).forEach(function(key) { payload[key] = baseParams[key]; });

        try {
          // keepalive: the permalink navigation follows immediately
          fetch(capiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            keepalive: true
          }).catch(function() {});
        } catch (e) {}
      }

      sendPair('EB_CTA_Click', false);
      sendPair('AddToCart', true);
    },

    /**
     * Plain cart-permalink links (announcement-bar CTA, early-bird save
     * badges and option CTAs) navigate without startReplaceCartCheckout,
     * so they'd emit no Meta events. Delegated capture-phase listener
     * fires the same browser+CAPI pairs before navigation; the CAPI leg
     * uses keepalive so it survives the page unload.
     */
    initCartPermalinkTracking: function() {
      var self = this;
      document.addEventListener('click', function(event) {
        var link = event.target && event.target.closest ? event.target.closest('a[href*="/cart/"]') : null;
        if (!link) return;
        var match = (link.getAttribute('href') || '').match(/\/cart\/(\d+):\d+/);
        if (!match) return;

        var price = parseInt(link.getAttribute('data-variant-price'), 10);
        self.trackDirectCheckoutEvents({
          variantId: match[1],
          variantPrice: isNaN(price) ? 0 : price
        });
      }, true);
    },

    startReplaceCartCheckout: function(options) {
      var self = this;
      var settings = options || {};
      var logPrefix = settings.logPrefix || '[Replace Cart Checkout]';
      var variantId = parseInt(settings.variantId, 10);

      if (!variantId || Number.isNaN(variantId)) {
        return Promise.reject(new Error('Invalid variant ID'));
      }

      self.trackDirectCheckoutEvents({
        variantId: variantId,
        variantPrice: settings.variantPrice,
        currency: settings.currency
      });

      var attributes = self.buildDirectCheckoutAttributes() || {};
      var permalink = '/cart/' + variantId + ':1';
      var pairs = [];

      Object.keys(attributes).forEach(function(key) {
        var value = attributes[key];
        if (value === undefined || value === null || value === '') return;
        pairs.push('attributes[' + encodeURIComponent(key) + ']=' + encodeURIComponent(String(value)));
      });

      if (pairs.length) {
        permalink += '?' + pairs.join('&');
      }

      return self.clearCart().then(function() {
        console.log(logPrefix, 'Cart cleared; redirecting to single-item checkout permalink');
        window.location.href = permalink;
      });
    },

    /**
     * Initialize Quick Buy functionality
     * Redirects to product page
     */
    initQuickBuy: function() {
      console.log('Quick Buy: Initializing...');
      var quickBuyButtons = document.querySelectorAll('[data-quick-buy]');
      console.log('Quick Buy: Found', quickBuyButtons.length, 'button(s)');
      
      if (quickBuyButtons.length === 0) {
        console.warn('Quick Buy: No buttons found with [data-quick-buy] attribute');
        return;
      }

      quickBuyButtons.forEach(function(button) {
        // 防止重复绑定
        if (button.dataset.quickBuyInitialized === 'true') {
          console.log('Quick Buy: Button already initialized, skipping');
          return;
        }
        button.dataset.quickBuyInitialized = 'true';

        button.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation(); // 阻止事件冒泡
          
          console.log('Quick Buy: Click event triggered');
          
          var productHandle = button.getAttribute('data-quick-buy');
          console.log('Quick Buy: Redirecting to product page:', productHandle);
          
          // Redirect to product page (works for both local and production)
          window.location.href = '/products/' + productHandle;
        });
      });
    },

    /**
     * Utility: Throttle function
     */
    throttle: function(func, limit) {
      var inThrottle;
      return function() {
        var args = arguments;
        var context = this;
        if (!inThrottle) {
          func.apply(context, args);
          inThrottle = true;
          setTimeout(function() {
            inThrottle = false;
          }, limit);
        }
      };
    }
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      MemoketTheme.init();
    });
  } else {
    MemoketTheme.init();
  }

  // Expose globally
  window.MemoketTheme = MemoketTheme;
})();
